import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

interface PermissionButtonProps {
  resource: string;
  action: 'create' | 'update' | 'delete' | 'read';
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'danger';
}

export const PermissionButton: React.FC<PermissionButtonProps> = ({
  resource,
  action,
  onClick,
  children,
  className = '',
  type = 'button',
  variant = 'primary'
}) => {
  const { hasPermission, loading } = usePermissions();
  const permitted = hasPermission(resource, action);

  const getVariantClasses = () => {
    if (!permitted) {
      return 'bg-monday-status-neutral text-white cursor-not-allowed';
    }

    switch (variant) {
      case 'primary':
        return 'bg-primary-500 hover:bg-primary-600 text-white';
      case 'secondary':
        return 'bg-white hover:bg-monday-header text-monday-text border border-monday-border';
      case 'danger':
        return 'bg-error-500 hover:bg-error-600 text-white';
      default:
        return 'bg-primary-500 hover:bg-primary-600 text-white';
    }
  };

  const getTooltipText = () => {
    switch (action) {
      case 'create':
        return 'You do not have permission to create records';
      case 'update':
        return 'You do not have permission to edit records';
      case 'delete':
        return 'You do not have permission to delete records';
      case 'read':
        return 'You do not have permission to view this';
      default:
        return 'Insufficient permissions';
    }
  };

  if (loading) {
    return (
      <button
        type={type}
        disabled
        className={`px-4 py-2 rounded ${className}`}
      >
        {children}
      </button>
    );
  }

  return (
    <div className="relative inline-block group">
      <button
        type={type}
        onClick={permitted ? onClick : undefined}
        disabled={!permitted}
        className={`px-4 py-2 rounded font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-200 transition-colors duration-150 ${getVariantClasses()} ${className}`}
      >
        {children}
      </button>
      {!permitted && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-monday-sidebar text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150 whitespace-nowrap z-10">
          {getTooltipText()}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-4 border-transparent border-t-monday-sidebar"></div>
          </div>
        </div>
      )}
    </div>
  );
};
